import FormControl from "@mui/material/FormControl";
import FormGroup from "@mui/material/FormGroup";
import { PSYCH_CARE_REHAB_SPECIALIZATIONS } from "core/consts";
import { useGetOntology } from "core/model/utils/ontologies/hooks";
import { getAriaDescribedByValue } from "core/model/utils/strings";
import CheckboxInputField from "ds_legacy/components/CheckboxInputField";
import { labelWithOptionalRequiredIcon } from "ds_legacy/components/RequiredIcon";
import { Validation } from "ds_legacy/components/Validation";
import { margin } from "ds_legacy/materials/metrics";
import { FONT_SIZE_14, Subheading } from "ds_legacy/materials/typography";
import { FormWatcher, StateDispatcher, isValid } from "react-forms-state";
import { useTranslations } from "translations";

const ELEMENT_NAME = "specializations";
const errorTextId = "psych-care-rehab-specializations-error-text-id";

function convertIn(
  inValue: number | number[] | null | undefined,
  props: { items: { id: number }[] },
): Array<{ id: string; label: string; value: boolean }> {
  const inputArray = inValue == null ? [] : [inValue].flat();
  return props.items.map((v) => ({
    id: v.id.toString(),
    label: "",
    value: inputArray.includes(v.id),
  }));
}

function convertOut(outValue: AnyObject) {
  const asArray: Array<{ id: string | number; value: boolean }> = Array.isArray(
    outValue,
  )
    ? outValue
    : Array.from(outValue as Iterable<any>);

  const values = asArray
    .filter((v) => v.value)
    .map((v) => (typeof v.id === "number" ? v.id : parseInt(v.id, 10)))
    .filter((v) => !isNaN(v));
  return values.length > 0 ? values : null;
}

function PsychiatricCareRehabSpecializationsSelectInner({
  items,
}: {
  items: Array<{ id: number; label: string }>;
}) {
  const translations = useTranslations();
  const getOntology = useGetOntology();

  const itemsWithLabels = items.map((item) => ({
    id: item.id,
    label: item.label || getOntology({ key: item.id, type: "specializations" }),
  }));

  return (
    <FormWatcher watchPath={ELEMENT_NAME}>
      {({ watchedValidation }) => {
        const fieldValid = isValid(watchedValidation);

        return (
          <FormControl
            component="fieldset"
            error={!fieldValid}
            style={{ margin: margin(0) }}
          >
            <Subheading
              bold
              error={!fieldValid}
              margin={margin(0, 0, 1, 0)}
              style={{ fontSize: FONT_SIZE_14 }}
            >
              {labelWithOptionalRequiredIcon({
                isRequired: true,
                label: translations.ontologies.specializations.key,
              })}
            </Subheading>
            <FormGroup sx={{ flexDirection: "column" }}>
              {itemsWithLabels.map((item) => (
                <CheckboxInputField
                  key={item.id}
                  elementName={item.id.toString()}
                  hasFieldValidation={fieldValid}
                  inputProps={{
                    "aria-describedby":
                      getAriaDescribedByValue([!fieldValid && errorTextId]) ||
                      undefined,
                  }}
                  isGroup
                  label={item.label}
                />
              ))}
            </FormGroup>
            <Validation
              elementName={ELEMENT_NAME}
              hasCustomValidation
              id={errorTextId}
              validation={watchedValidation}
            />
          </FormControl>
        );
      }}
    </FormWatcher>
  );
}

const ConnectedPsychiatricCareRehabSpecializationsSelect = StateDispatcher(
  convertIn,
  convertOut,
)<{
  elementName?: string;
  items: Array<{ id: number; label: string }>;
  required?: boolean;
}>(PsychiatricCareRehabSpecializationsSelectInner);

export function PsychiatricCareRehabSpecializationsSelect() {
  return (
    <ConnectedPsychiatricCareRehabSpecializationsSelect
      elementName={ELEMENT_NAME}
      items={PSYCH_CARE_REHAB_SPECIALIZATIONS.map((id) => ({ id, label: "" }))}
      required
    />
  );
}
